import { useState, useRef } from "react";
import { api, apiErr, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const CARDS_TEMPLATE = [
  ["Course", "Module", "Topic", "Deck", "Question", "Answer", "Tags", "Specification", "Image", "ExternalID"],
  ["OCR A A-Level Biology", "Module 2: Foundations in Biology", "Enzymes", "Enzymes Core", "What is an enzyme?", "A biological catalyst (protein) that lowers activation energy.", "enzymes", "3.1.1", "", "BIO-ENZ-001"],
];

export default function Imports() {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [skipDup, setSkipDup] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const runPreview = async (f) => {
    setBusy(true); setPreview(null); setDone(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/commit", { batch_id: preview.batch_id, skip_duplicates: skipDup });
      setDone(data);
      setPreview(null); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(`Imported ${data.created} cards`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Bulk import</h1>
        <p className="mt-1 text-slate-500">Upload a CSV or XLSX. Columns: Course, Module, Topic, Deck, Question, Answer, Tags, Specification, Image, ExternalID.</p>
      </div>

      <label className="block rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center cursor-pointer hover:border-slate-400 transition-colors" data-testid="import-dropzone">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="import-file-input" />
        <Upload className="h-8 w-8 text-slate-400 mx-auto" />
        <div className="mt-3 font-medium text-slate-700">{file ? file.name : "Choose a file to import"}</div>
        <div className="text-sm text-slate-400 mt-1">CSV or XLSX</div>
      </label>

      {busy && !preview && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

      {preview && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat value={preview.valid} label="Valid cards" tone="emerald" />
            <Stat value={preview.error_count} label="Rows with errors" tone={preview.error_count ? "rose" : "slate"} />
            <Stat value={preview.duplicates} label="Duplicates" tone="amber" />
            <Stat value={preview.new_topics} label="New topics" />
          </div>
          <p className="text-sm text-slate-500">{preview.new_courses} new courses, {preview.new_modules} new modules, {preview.new_decks} new decks will be created.</p>

          {preview.errors.length > 0 && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 max-h-48 overflow-auto">
              <div className="flex items-center gap-2 text-rose-700 font-medium text-sm mb-2"><AlertTriangle className="h-4 w-4" /> Row errors</div>
              {preview.errors.map((e) => (
                <div key={e.row} className="text-sm text-rose-600" data-testid={`import-error-${e.row}`}>Row {e.row}: {e.messages.join(", ")}</div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch checked={skipDup} onCheckedChange={setSkipDup} data-testid="skip-duplicates-switch" />
            <Label>Skip duplicate cards</Label>
          </div>

          <Button onClick={commit} disabled={busy || preview.valid === 0} data-testid="commit-import-button" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${skipDup ? preview.valid - preview.duplicates : preview.valid} cards`}
          </Button>
        </div>
      )}

      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 flex items-center gap-3" data-testid="import-done">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          <div><div className="font-medium text-emerald-800">Import complete</div>
            <div className="text-sm text-emerald-700">{done.created} cards created, {done.skipped} skipped.</div></div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-3">
        <FileSpreadsheet className="h-5 w-5 text-slate-400 mt-0.5" />
        <div className="text-sm text-slate-500 flex-1">
          <p className="font-medium text-slate-700">Template</p>
          <p className="mt-1 font-mono text-xs break-all">Course,Module,Topic,Deck,Question,Answer,Tags,Specification,Image,ExternalID</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadCsv("science-sorted-cards-template.csv", CARDS_TEMPLATE)} data-testid="download-cards-template">
          <Download className="h-4 w-4 mr-2" /> Download template
        </Button>
      </div>
    </div>
  );
}

function Stat({ value, label, tone = "slate" }) {
  const c = { emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600", slate: "text-slate-900" }[tone];
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className={`font-display text-2xl font-bold ${c}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
